// src/components/prestige/settings.js  (Rules & Settings)
import React, { useState, useEffect, useMemo } from "react";
import {
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  Save,
  Store,
  PenLine,
  Banknote,
  Loader2,
  SlidersHorizontal,
  Clock,
  Sun,
  Moon,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Info,
} from "lucide-react";
import axios from "../../utils/axiosDash";

// Frontend-only password check
const SETTINGS_PASSWORD = "Citriot@2026";

const OUTLETS = ["Church Street", "Kammanahalli", "Channapatna"];

const ROLES = [
  { key: "writer", label: "Writer", icon: PenLine, tint: "bg-violet-50 text-violet-600" },
  { key: "cashier", label: "Cashier", icon: Banknote, tint: "bg-emerald-50 text-emerald-600" },
];

const RANGES = [
  {
    key: "workingHours",
    label: "Working Hours",
    icon: Clock,
    iconTint: "bg-slate-100 text-slate-600",
  },
  { key: "shift1", label: "Shift 1", icon: Sun, iconTint: "bg-sky-50 text-sky-600" },
  { key: "shift2", label: "Shift 2", icon: Moon, iconTint: "bg-indigo-50 text-indigo-600" },
];

// Default: 11 AM – 2:30 AM, Shift 1 = 11 AM – 7 PM, Shift 2 = 7 PM – 2:30 AM
const defaultRole = () => ({
  workingHours: { start: "11:00", end: "02:30" },
  shift1: { start: "11:00", end: "19:00" },
  shift2: { start: "19:00", end: "02:30" },
});

const buildDefaults = () => {
  const out = {};
  OUTLETS.forEach((o) => {
    out[o] = { writer: defaultRole(), cashier: defaultRole() };
  });
  return out;
};

// Saved values on top of defaults, so a missing outlet/role never breaks the page
const mergeSettings = (saved) => {
  const base = buildDefaults();
  if (!saved || typeof saved !== "object") return base;
  OUTLETS.forEach((o) =>
    ROLES.forEach(({ key }) =>
      RANGES.forEach(({ key: r }) => {
        const s = saved?.[o]?.[key]?.[r];
        if (s?.start && s?.end) base[o][key][r] = { start: s.start, end: s.end };
      })
    )
  );
  return base;
};

// ─────────────────────────────────────────────
// Time helpers
// ─────────────────────────────────────────────
const toMin = (t) => {
  const [h, m] = (t || "00:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Minutes between start and end; end <= start means it runs past midnight
const durationMin = (range) => {
  const d = (toMin(range.end) - toMin(range.start) + 1440) % 1440;
  return d === 0 ? 1440 : d;
};

const fmtDuration = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  return h ? `${h}h` : `${m}m`;
};

const fmt12 = (t) => {
  const mins = toMin(t);
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
};

const endsNextDay = (range) => range.end <= range.start;

// ─────────────────────────────────────────────
// Password gate
// ─────────────────────────────────────────────
const PasswordGate = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError("Please enter the password");
      return;
    }
    if (password === SETTINGS_PASSWORD) {
      onUnlock();
    } else {
      setError("Incorrect password");
      setPassword("");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-60px)] px-4 bg-gradient-to-b from-slate-50 to-blue-50/40">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-lg shadow-slate-200/60 p-7"
      >
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mb-4 shadow-md shadow-blue-600/30">
            <Lock size={24} className="text-white" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Rules &amp; Settings</h2>
          <p className="text-[13px] text-slate-500 mt-1">
            Only authorised users can change settings.
Enter the password to continue.
          </p>
        </div>

        <label className="block text-[12px] font-semibold text-slate-600 mb-1.5">Password</label>
        <div className="relative">
          <input
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            autoFocus
            placeholder="Enter password"
            className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border bg-slate-50 text-[14px] text-slate-800 outline-none transition-all focus:bg-white focus:ring-4 ${
              error
                ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                : "border-slate-200 focus:border-blue-500 focus:ring-blue-100"
            }`}
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {error && (
          <p className="flex items-center gap-1.5 text-[12px] text-red-600 mt-2">
            <AlertTriangle size={13} /> {error}
          </p>
        )}

        <button
          type="submit"
          className="w-full mt-5 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all shadow-sm shadow-blue-600/30"
        >
          <ShieldCheck size={16} />
          Unlock
        </button>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────
// Time picker: hour / minute / AM-PM dropdowns (always a valid time)
// ─────────────────────────────────────────────
const selectClass =
  "h-9 px-2 rounded-lg border border-slate-300 bg-white text-[13px] font-medium text-slate-800 outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-100 cursor-pointer";

const HOURS_12 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 00 – 59

const TimeInput = ({ label, value, onChange }) => {
  const total = toMin(value);
  const h24 = Math.floor(total / 60);
  const minute = total % 60;
  const isPM = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

  const emit = (nextH12, nextMinute, nextPM) => {
    const hh = (nextH12 % 12) + (nextPM ? 12 : 0);
    onChange(`${String(hh).padStart(2, "0")}:${String(nextMinute).padStart(2, "0")}`);
  };

  return (
    <div>
      {/* label only on small screens; on desktop the column header is used */}
      <span className="md:hidden block mb-1 text-[11px] font-semibold text-slate-500">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <select
          value={h12}
          onChange={(e) => emit(Number(e.target.value), minute, isPM)}
          className={`${selectClass} w-[62px]`}
          aria-label={`${label} hour`}
        >
          {HOURS_12.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span className="text-slate-400 font-semibold">:</span>
        <select
          value={minute}
          onChange={(e) => emit(h12, Number(e.target.value), isPM)}
          className={`${selectClass} w-[62px]`}
          aria-label={`${label} minutes`}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, "0")}
            </option>
          ))}
        </select>
        <select
          value={isPM ? "PM" : "AM"}
          onChange={(e) => emit(h12, minute, e.target.value === "PM")}
          className={`${selectClass} w-[68px]`}
          aria-label={`${label} AM or PM`}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// One row: Working Hours / Shift 1 / Shift 2
// ─────────────────────────────────────────────
const ROW_GRID = "md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_72px]";

const RangeRow = ({ meta, value, onChange }) => {
  const Icon = meta.icon;
  return (
    <div
      className={`grid grid-cols-1 ${ROW_GRID} gap-3 md:gap-4 md:items-center px-5 py-4 border-t border-slate-100`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${meta.iconTint}`}>
          <Icon size={17} />
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-slate-900 leading-tight">{meta.label}</p>
          <p className="text-[12px] text-slate-500 truncate">
            {fmt12(value.start)} – {fmt12(value.end)}
            {endsNextDay(value) ? " (next day)" : ""}
          </p>
        </div>
      </div>

      <TimeInput label="Start time" value={value.start} onChange={(v) => onChange({ ...value, start: v })} />
      <TimeInput label="End time" value={value.end} onChange={(v) => onChange({ ...value, end: v })} />

      <span className="justify-self-start md:justify-self-end inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[12px] font-semibold text-slate-600">
        {fmtDuration(durationMin(value))}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────
// One role (Writer / Cashier) for the selected outlet
// ─────────────────────────────────────────────
const RoleCard = ({ role, data, onChange }) => {
  const Icon = role.icon;

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${role.tint}`}>
          <Icon size={19} />
        </div>
        <div>
          <h3 className="text-[16px] font-bold text-slate-900 leading-tight">{role.label}</h3>
          <p className="text-[12px] text-slate-500">Working hours and shift timings</p>
        </div>
      </div>

      {/* Column headers (desktop) */}
      <div
        className={`hidden md:grid ${ROW_GRID} gap-4 px-5 py-2 bg-slate-50 border-t border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500`}
      >
        <span>Timing</span>
        <span>Start time</span>
        <span>End time</span>
        <span className="text-right">Duration</span>
      </div>

      {RANGES.map((meta) => (
        <RangeRow
          key={meta.key}
          meta={meta}
          value={data[meta.key]}
          onChange={(next) => onChange({ ...data, [meta.key]: next })}
        />
      ))}
    </section>
  );
};

// ─────────────────────────────────────────────
// Settings content (after unlock)
// ─────────────────────────────────────────────
const SettingsContent = () => {
  const [settings, setSettings] = useState(buildDefaults());
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(buildDefaults()));
  const [activeOutlet, setActiveOutlet] = useState(OUTLETS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      let loaded = buildDefaults();
      try {
        const organizationId = localStorage.getItem("organizationId");
        const res = await axios.post("/fetch-empire-settings", { organizationId });
        loaded = mergeSettings(res?.data?.data?.outlets);
      } catch (err) {
        console.error("Error fetching settings:", err);
      } finally {
        setSettings(loaded);
        setSavedSnapshot(JSON.stringify(loaded));
        setLoading(false);
      }
    };
    load();
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(settings) !== savedSnapshot,
    [settings, savedSnapshot]
  );

  const updateRole = (roleKey, next) => {
    setMessage(null);
    setSettings((prev) => ({
      ...prev,
      [activeOutlet]: { ...prev[activeOutlet], [roleKey]: next },
    }));
  };

  // Copy the current outlet's timings to the other two outlets
  const copyToAll = () => {
    setSettings((prev) => {
      const source = prev[activeOutlet];
      const next = {};
      OUTLETS.forEach((o) => {
        next[o] = JSON.parse(JSON.stringify(source));
      });
      return next;
    });
    setMessage({
      type: "info",
      text: `Copied ${activeOutlet} timings to all outlets. Press Save to apply.`,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const organizationId = localStorage.getItem("organizationId");
      let username = "";
      try {
        username = JSON.parse(localStorage.getItem("user"))?.username || "";
      } catch (e) {
        // ignore
      }
      await axios.post("/save-empire-settings", {
        organizationId,
        username,
        outlets: settings,
      });
      setSavedSnapshot(JSON.stringify(settings));
      setMessage({ type: "success", text: "Settings saved for all outlets." });
    } catch (err) {
      console.error("Error saving settings:", err);
      setMessage({
        type: "error",
        text: err?.response?.data?.message || "Could not save settings. Try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const messageStyles = {
    success: "bg-green-50 text-green-700 border-green-200",
    error: "bg-red-50 text-red-700 border-red-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const MessageIcon =
    message?.type === "success" ? CheckCircle2 : message?.type === "error" ? AlertTriangle : Info;

  return (
    <div className="min-h-[calc(100vh-60px)] flex flex-col">
    <div className="flex-1 p-4 sm:p-6 pb-6 max-w-5xl w-full mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center shadow-md shadow-blue-600/25">
          <SlidersHorizontal size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">Rules &amp; Settings</h1>
          <p className="text-[13px] text-slate-500">
            Working hours and shift timings for Writer and Cashier at each outlet
          </p>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <span
            className={`hidden sm:flex items-center gap-2 text-[13px] font-medium ${
              dirty ? "text-amber-600" : "text-slate-400"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${dirty ? "bg-amber-500" : "bg-emerald-400"}`}
            />
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-blue-600/30"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium border ${
            messageStyles[message.type]
          }`}
        >
          <MessageIcon size={15} className="shrink-0" />
          {message.text}
        </div>
      )}

      {/* Outlet selector */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="inline-flex flex-wrap gap-1.5 p-1.5 rounded-xl bg-white border border-slate-200 shadow-sm">
          {OUTLETS.map((outlet) => {
            const active = outlet === activeOutlet;
            return (
              <button
                key={outlet}
                onClick={() => setActiveOutlet(outlet)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                  active
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-600/30"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Store size={15} />
                {outlet}
              </button>
            );
          })}
        </div>

        <button
          onClick={copyToAll}
          className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition-all"
          title="Copy this outlet's timings to all outlets"
        >
          <Copy size={14} />
          Copy to all outlets
        </button>
      </div>

      {/* Role cards */}
      <div className="flex flex-col gap-5">
        {ROLES.map((role) => (
          <RoleCard
            key={`${activeOutlet}-${role.key}`}
            role={role}
            data={settings[activeOutlet][role.key]}
            onChange={(next) => updateRole(role.key, next)}
          />
        ))}
      </div>

    </div>
    </div>
  );
};

// Unlock is remembered for this browser tab (sessionStorage):
//  - refreshing the page keeps you inside Rules & Settings
//  - opening another menu item locks it again, so the password is asked next time
//  - closing the tab also clears it
const UNLOCK_KEY = "empireRulesSettingsUnlocked";

const readUnlocked = () => {
  try {
    return sessionStorage.getItem(UNLOCK_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const Settings = () => {
  const [unlocked, setUnlocked] = useState(readUnlocked);

  useEffect(() => {
    if (!unlocked) return undefined;
    try {
      sessionStorage.setItem(UNLOCK_KEY, "1");
    } catch (e) {
      // ignore (private mode etc.)
    }
    // runs only when leaving the module, not on a page refresh
    return () => {
      try {
        sessionStorage.removeItem(UNLOCK_KEY);
      } catch (e) {
        // ignore
      }
    };
  }, [unlocked]);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  return <SettingsContent />;
};

export default Settings;